'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';

import VideoPlayerModal from '@/components/VideoPlayerModal';
import AdSenseUnit from '@/components/AdSenseUnit';
import AdDisplay from '@/components/AdDisplay';
import NavBar from '@/components/NavBar';
import { createClient } from '@/lib/supabase/client';
import {
  Video,
  MOCK_VIDEOS,
  VIDEO_CATEGORIES,
  formatViews,
} from '@/data/videos';

const CLIPS_CATEGORIES = ['All', ...VIDEO_CATEGORIES];
const PAGE_SIZE = 24;

// Short-form / clip-style categories to highlight in hero
const CLIP_TAGS = ['WILD', 'CAUGHT IN 4K', 'UNREAL', 'PLOT TWIST', 'SHE SNAPPED', 'GOATED', 'BARS ON BARS', 'NOT TODAY'];

export default function ClipsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [clips, setClips] = useState<Video[]>([]);
  const [clipsLoading, setClipsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const [heroIndex, setHeroIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const mapDbVideoToVideo = (row: any): Video => ({
    id: row.id,
    title: row.title || '',
    tag: row.tag || '',
    description: row.description || '',
    source: row.source || 'youtube',
    url: row.url || '',
    embedId: row.embed_id || undefined,
    thumbnail: row.thumbnail || '',
    thumbnailAlt: row.thumbnail_alt || '',
    views: row.views || 0,
    comments: row.comments_count || 0,
    submittedBy: row.submitted_by || '',
    submittedAt: row.submitted_at || row.created_at?.split('T')[0] || '',
    status: row.video_status || 'approved',
    category: row.category || '',
  });

  const fetchClips = useCallback(async (page: number, append: boolean) => {
    if (page === 0) setClipsLoading(true);
    else setLoadingMore(true);
    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('video_status', 'approved')
        .order('views', { ascending: false })
        .range(from, to);
      if (error) throw error;
      const mapped = data ? data.map(mapDbVideoToVideo) : [];
      if (page === 0 && mapped.length === 0) {
        setClips(MOCK_VIDEOS);
        setHasMore(false);
      } else {
        setClips(prev => append ? [...prev, ...mapped] : mapped);
        setHasMore(mapped.length === PAGE_SIZE);
      }
    } catch {
      if (page === 0) {
        setClips(MOCK_VIDEOS);
        setHasMore(false);
      }
    } finally {
      setClipsLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    fetchClips(0, false);
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    setHasMore(true);
    fetchClips(0, false);
  }, [activeCategory, searchQuery]);

  // Auto-rotate hero
  useEffect(() => {
    const heroClips = clips.slice(0, 5);
    if (heroClips.length < 2) return;
    const timer = setInterval(() => {
      setHeroIndex(prev => (prev + 1) % heroClips.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [clips.length]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !clipsLoading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          fetchClips(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, clipsLoading, currentPage, fetchClips]);

  const heroClips = clips.length > 0 ? clips.slice(0, 5) : MOCK_VIDEOS.slice(0, 5);
  const currentHero = heroClips[heroIndex] || heroClips[0];

  const filteredClips = clips.filter(clip => {
    const matchesCategory = activeCategory === 'All' || clip.category === activeCategory;
    const matchesSearch = !searchQuery ||
      clip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clip.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clip.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  return (
    <div className="min-h-screen bg-surface-primary font-condensed">
      {/* ===== HEADER ===== */}
      <NavBar
        activePage="clips"
        hasTicker={false}
        onMobileMenuChange={setMobileMenuOpen}
        rightSlot={
          <div className="relative">
            {showSearch ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search clips..."
                  className="bg-surface-secondary border border-stroke-primary text-text-primary placeholder-text-muted rounded-[4px] px-3 py-1.5 text-sm w-36 md:w-56 focus:outline-none focus:border-brand-red transition-colors"
                  autoFocus
                />
                <button
                  onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                  className="flex items-center justify-center w-9 h-9 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
                  aria-label="Close search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="flex items-center justify-center w-10 h-10 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
                aria-label="Open search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
          </div>
        }
        mobileMenuExtra={
          <>
            <p className="text-text-muted text-xs uppercase tracking-widest mb-2">Categories</p>
            {CLIPS_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setMobileMenuOpen(false); }}
                className={`w-full text-left text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 ${
                  activeCategory === cat ? 'text-brand-red' : 'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary'
                }`}
              >
                {cat === 'All' ? 'All Clips' : cat}
              </button>
            ))}
          </>
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
        <div className="flex gap-6">
          {/* ===== MAIN CONTENT ===== */}
          <div className="flex-1 min-w-0">

        {/* ===== HERO SECTION ===== */}
        {!searchQuery && activeCategory === 'All' && (
          <section className="mb-6">
            {/* Page title strip */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-text-primary text-3xl md:text-4xl font-normal">VIDDSTAR</span>
                <span className="text-brand-red font-bold text-3xl md:text-4xl">CLIPS</span>
              </div>
              <span className="bg-brand-red text-white text-xs font-bold uppercase px-2 py-1 rounded-[2px] tracking-wide">⚡ Short &amp; Viral</span>
            </div>

            {clipsLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-stroke-primary animate-pulse">
                <div className="lg:col-span-2 bg-surface-tertiary" style={{ aspectRatio: '16/9' }} />
                <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-stroke-primary flex flex-col">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex gap-3 p-3 border-b border-stroke-primary last:border-b-0">
                      <div className="flex-shrink-0 w-24 bg-surface-secondary rounded" style={{ aspectRatio: '16/9' }} />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3 bg-surface-secondary rounded w-full" />
                        <div className="h-3 bg-surface-secondary rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-stroke-primary">
                {/* Main hero clip */}
                <div
                  className="lg:col-span-2 relative cursor-pointer group overflow-hidden bg-surface-tertiary"
                  onClick={() => currentHero && setSelectedVideo(currentHero)}
                  style={{ aspectRatio: '16/9' }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && currentHero && setSelectedVideo(currentHero)}
                  aria-label={currentHero ? `Watch clip: ${currentHero.title}` : 'Featured clip'}
                >
                  {currentHero && (
                    <>
                      <img
                        src={currentHero.thumbnail}
                        alt={currentHero.thumbnailAlt}
                        className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-500"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.onerror = null;
                          target.src = '/assets/images/no_image.png';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      {/* Play button */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-xl">
                          <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      {/* Views badge */}
                      <div className="absolute top-3 right-3">
                        <span className="bg-black/70 text-white text-xs font-bold px-2 py-1 rounded-[2px] flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {formatViews(currentHero.views)}
                        </span>
                      </div>
                      {/* Title overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                        {currentHero.tag && (
                          <span className="block text-brand-red font-bold text-lg md:text-2xl mb-1">{currentHero.tag}</span>
                        )}
                        <h2 className="text-white text-xl md:text-3xl font-normal leading-tight line-clamp-2">
                          {currentHero.title}
                        </h2>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-0.5 uppercase tracking-wide rounded-[2px]">
                            {currentHero.category}
                          </span>
                        </div>
                      </div>
                      {/* Slide indicators */}
                      <div className="absolute bottom-3 right-4 flex gap-1.5">
                        {heroClips.map((_, i) => (
                          <button
                            key={i}
                            onClick={(e) => { e.stopPropagation(); setHeroIndex(i); }}
                            className={`rounded-full transition-all duration-300 ${
                              i === heroIndex ? 'w-6 h-2 bg-brand-red' : 'w-2 h-2 bg-white bg-opacity-50 hover:bg-opacity-80'
                            }`}
                            aria-label={`Go to clip ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Side clip list */}
                <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-stroke-primary flex flex-col">
                  {heroClips.map((clip, i) => (
                    <div
                      key={clip.id}
                      className={`flex gap-3 p-3 cursor-pointer group transition-colors duration-150 border-b border-stroke-primary last:border-b-0 ${
                        i === heroIndex ? 'bg-surface-secondary' : 'hover:bg-surface-secondary'
                      }`}
                      onClick={() => { setHeroIndex(i); setSelectedVideo(clip); }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedVideo(clip)}
                    >
                      <div className="flex-shrink-0 w-24 relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                        <img
                          src={clip.thumbnail}
                          alt={clip.thumbnailAlt}
                          className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.onerror = null;
                            target.src = '/assets/images/no_image.png';
                          }}
                        />
                        {i === heroIndex && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <div className="w-6 h-6 bg-brand-red rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-text-primary text-sm leading-snug line-clamp-3">
                          {clip.tag && <span className="tag-label mr-1 text-xs">{clip.tag} </span>}
                          {clip.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 text-text-secondary text-xs">
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {formatViews(clip.views)}
                          </span>
                          <span className="text-text-muted text-xs uppercase tracking-wide">{clip.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
        {/* ===== CATEGORY BAR ===== */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          {CLIPS_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[4px] transition-colors duration-150 border ${
                activeCategory === cat
                  ? 'bg-brand-red text-white border-brand-red' :'bg-surface-secondary text-text-secondary border-stroke-primary hover:text-text-primary hover:border-stroke-light'
              }`}
            >
              {cat === 'All' ? 'All Clips' : cat}
            </button>
          ))}
        </div>

        {/* ===== CLIPS GRID ===== */}
        {clipsLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-surface-tertiary rounded" style={{ aspectRatio: '16/9' }} />
                <div className="mt-2 space-y-1">
                  <div className="h-3 bg-surface-tertiary rounded w-full" />
                  <div className="h-3 bg-surface-tertiary rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredClips.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-text-muted text-lg">No clips found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredClips.map((clip, index) => (
              <React.Fragment key={clip.id}>
                <div
                  className="cursor-pointer group"
                  onClick={() => setSelectedVideo(clip)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedVideo(clip)}
                  aria-label={`Watch: ${clip.title}`}
                >
                  <div className="relative overflow-hidden bg-surface-tertiary rounded" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={clip.thumbnail}
                      alt={clip.thumbnailAlt}
                      className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.onerror = null;
                        target.src = '/assets/images/no_image.png';
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-1 right-1">
                      <span className="bg-black/70 text-white text-xs px-1.5 py-0.5 rounded-[2px]">
                        {formatViews(clip.views)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1.5">
                    <h3 className="text-text-primary text-xs leading-snug line-clamp-2">
                      {clip.tag && <span className="text-brand-red font-bold mr-1">{clip.tag}</span>}
                      {clip.title}
                    </h3>
                    <p className="text-text-muted text-xs mt-0.5 uppercase tracking-wide">{clip.category}</p>
                  </div>
                </div>
                {/* Between-videos ad: inject after every 8th clip, spanning full grid width */}
                {(index + 1) % 8 === 0 && index + 1 < filteredClips.length && (
                  <div className="col-span-2 sm:col-span-3 md:col-span-4 w-full my-1">
                    <AdDisplay placement="between-videos" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Load more sentinel */}
        <div ref={loadMoreRef} className="h-10 flex items-center justify-center mt-4">
          {loadingMore && <span className="text-text-muted text-sm">Loading more...</span>}
        </div>

          </div>{/* end flex-1 min-w-0 */}

          {/* ===== ADS SIDEBAR ===== */}
          <aside className="hidden lg:flex flex-col gap-4 w-[300px] flex-shrink-0">
            <div className="sticky top-[80px] flex flex-col gap-4">
              <AdDisplay placement="sidebar" />
              <AdSenseUnit
                adSlot="DDDDDDDDDD"
                adFormat="rectangle"
                label="Advertisement"
              />
              <AdDisplay placement="sidebar" className="mt-2" />
            </div>
          </aside>
        </div>
      </main>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-stroke-primary bg-surface-secondary mt-12">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-text-muted text-xs">&copy; {new Date().getFullYear()} ViddStar. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/" className="text-text-muted hover:text-brand-red text-xs transition-colors">ViddStarTV</Link>
            <Link href="/news" className="text-text-muted hover:text-brand-red text-xs transition-colors">News</Link>
            <Link href="/exclusive" className="text-text-muted hover:text-brand-red text-xs transition-colors">Exclusive</Link>
          </div>
        </div>
      </footer>

      {/* ===== MODALS ===== */}
      {selectedVideo && (
        <VideoPlayerModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
}
